import * as React from 'react'
import { v4 as uuid4 } from 'uuid'

import { Modal } from './modal'
import { ModalAction, UserConfig } from './types'

export interface ModalItem {
  id: string
  e: React.ReactNode
}

export const useModals = () => {
  const [modals, setModals] = React.useState<ModalItem[]>([])

  const add = (modal: React.ReactNode, id: string): (() => void) => {
    if (modals.find((x) => x.id === id) == null) {
      setModals((state) => [...state, { id, e: modal }])
    }

    return () => remove(id)
  }

  const remove = (id: string) =>
    setModals((state) => state.filter((x) => x.id !== id))

  // TODO use case?
  const getStackingOrder = (id: string) => {
    const item = modals.find((x) => x.id === id)
    return item != null ? modals.indexOf(item) : -1
  }

  const show = (
    { id = uuid4(), ...config }: UserConfig,
    onConfirm: () => ModalAction = () => 'CLOSE'
  ) => add(<Modal {...config} onConfirm={onConfirm} />, id)

  const showCancellable = (
    { id = uuid4(), ...config }: UserConfig,
    onConfirm: () => ModalAction = () => 'CLOSE',
    onCancel: () => ModalAction = () => 'CLOSE'
  ) => add(<Modal {...config} onConfirm={onConfirm} onCancel={onCancel} />, id)

  const showCustom = (x: React.ReactNode, id: string = uuid4()) => add(x, id)

  return {
    getStackingOrder,
    modals,
    remove,
    show,
    showCancellable,
    showCustom
  }
}

export type ModalsApi = ReturnType<typeof useModals>
const ModalsCtx = React.createContext<ModalsApi | null>(null)

export const Modals = {
  Provider: ModalsCtx.Provider,
  use: (): ModalsApi => {
    const ctx = React.useContext(ModalsCtx)
    if (ctx === null) {
      throw new Error('Must be wrapped within Modals.Provider')
    }
    return ctx
  }
}
